/**
 * /solutions/explore — your Explore chats; also Validation › 1st line support
 * (`surface="support"`, same list scoped to support chats — see `surface.ts`).
 *
 * A chat is a saved conversation **grounded in one or more collections**: every
 * answer comes only from those documents, with citations. New chat = a name +
 * the collections to answer from (the picker offers exactly the collections the
 * backend will let you query — yours plus those shared with you) + the Azure AI
 * Foundry connection and model that writes the answers (`ChatModelPicker`,
 * server-driven: only connections you may use). Retrieval always runs on the
 * platform AI plane.
 *
 * Chats are private: the list shows your own plus any explicitly shared with
 * you (read-only). A chat from before Explore has no collection; it is marked
 * and can be continued once one is attached.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Modal,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
  Tooltip,
} from "@mantine/core";
import { IconMessagePlus, IconMessages, IconTrash } from "@tabler/icons-react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { createdOn } from "@/features/projects/creatorMeta";
import {
  ChatModelPicker,
  CollectionDocumentPicker,
  chatModelLabel,
  defaultChoice,
  useChatModels,
  type ChatModelChoice,
  type CollectionSelection,
} from "@/features/solutions/shared";
import { createChat, deleteChat, listChats, type ChatOut } from "@/shared/api/ai";
import { listCardActionStyle, listCardStyle } from "@/shared/ui/listCard";
import { toastApiError, toastSuccess } from "@/shared/ui/toast";
import { SURFACES, type ChatSurface } from "./surface";

export function ExploreList({ surface = "explore" }: { surface?: ChatSurface }) {
  const config = SURFACES[surface];
  const getToken = useAccessToken();
  const navigate = useNavigate();

  const [chats, setChats] = useState<ChatOut[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [collections, setCollections] = useState<CollectionSelection[]>([]);
  const [busy, setBusy] = useState(false);
  const [choice, setChoice] = useState<ChatModelChoice>({ connectionId: null, deployment: null });
  const models = useChatModels(creating);
  const modelOptions = models.options;

  // Default to the first connection / first model once the picklist arrives.
  useEffect(() => {
    if (modelOptions && !choice.connectionId) setChoice(defaultChoice(modelOptions));
  }, [modelOptions, choice.connectionId]);

  const load = useCallback(async () => {
    try {
      const token = await getToken();
      setChats((await listChats(token, surface)).items);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load chats.");
      setChats([]);
    }
  }, [getToken, surface]);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => {
    setName("");
    setCollections([]);
    setChoice(
      modelOptions ? defaultChoice(modelOptions) : { connectionId: null, deployment: null },
    );
    setCreating(true);
  };

  const onCreate = async () => {
    if (!choice.connectionId || !choice.deployment) return;
    setBusy(true);
    try {
      const token = await getToken();
      const chat = await createChat(token, {
        name: name.trim(),
        index_ids: collections.map((c) => c.indexId),
        connection_id: choice.connectionId,
        deployment: choice.deployment,
        surface,
      });
      setCreating(false);
      navigate(`${config.basePath}/${chat.chat_id}`);
    } catch (err) {
      toastApiError("Could not create chat", err);
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (chat: ChatOut) => {
    setBusy(true);
    try {
      const token = await getToken();
      await deleteChat(token, chat.chat_id);
      toastSuccess("Chat deleted", `${chat.name} is gone.`);
      await load();
    } catch (err) {
      toastApiError("Could not delete chat", err);
    } finally {
      setBusy(false);
    }
  };

  const canCreate = useMemo(
    () =>
      Boolean(
        name.trim() && collections.length > 0 && choice.connectionId && choice.deployment && !busy,
      ),
    [name, collections, choice, busy],
  );

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center">
        <Title order={2}>{config.title}</Title>
        <Button leftSection={<IconMessagePlus size={16} />} onClick={openCreate}>
          New chat
        </Button>
      </Group>
      <Text c="dimmed" size="sm">
        {config.description}
      </Text>

      {error && (
        <Alert variant="light" color="red" title="Could not load chats">
          {error}
        </Alert>
      )}

      {chats === null ? (
        <Group justify="center" p="xl">
          <Loader size="sm" />
        </Group>
      ) : chats.length === 0 ? (
        <Paper withBorder radius="md" p="xl">
          <Text c="dimmed" ta="center">
            No chats yet. Start one and pick the collections it should answer from.
          </Text>
        </Paper>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg" verticalSpacing="lg">
          {chats.map((chat) => (
            <Card
              key={chat.chat_id}
              withBorder
              radius="md"
              padding="lg"
              style={{
                ...listCardStyle,
                borderTop: "3px solid var(--mantine-primary-color-filled)",
              }}
            >
              <Group gap="sm" mb="xs" wrap="nowrap" justify="space-between">
                <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                  <ThemeIcon variant="light" size={32} radius="md">
                    <IconMessages size={18} />
                  </ThemeIcon>
                  <Text fw={600} truncate>
                    {chat.name}
                  </Text>
                </Group>
                {!chat.is_owner && (
                  <Tooltip label="Shared with you — read only">
                    <Badge color="gray" variant="light" size="sm">
                      Shared
                    </Badge>
                  </Tooltip>
                )}
              </Group>
              <Group gap={4}>
                {chat.grounded ? (
                  (chat.collections ?? []).map((c) => (
                    <Badge key={c.index_id} variant="outline" size="sm" radius="sm" tt="none">
                      {c.name}
                    </Badge>
                  ))
                ) : (
                  <Badge color="orange" variant="light" size="sm" radius="sm" tt="none">
                    No collection — attach one to continue
                  </Badge>
                )}
              </Group>
              <Text size="xs" c="dimmed" mt="sm" data-testid="chat-model">
                {chatModelLabel(chat)}
              </Text>
              <Text size="xs" c="dimmed">
                {chat.message_count} {chat.message_count === 1 ? "message" : "messages"} · updated{" "}
                {createdOn(chat.updated_at_utc)}
                {!chat.is_owner &&
                  ` · owner ${chat.owner_display_name ?? chat.owner_email ?? "unknown"}`}
              </Text>
              <Group justify="space-between" mt="md" style={listCardActionStyle}>
                <Button
                  variant="light"
                  size="xs"
                  component={RouterLink}
                  to={`${config.basePath}/${chat.chat_id}`}
                >
                  Open
                </Button>
                {chat.is_owner && (
                  <Tooltip label="Delete chat">
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      onClick={() => void onDelete(chat)}
                      disabled={busy}
                      aria-label={`Delete ${chat.name}`}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
            </Card>
          ))}
        </SimpleGrid>
      )}

      <Modal opened={creating} onClose={() => setCreating(false)} title="New chat" centered>
        <Stack gap="md">
          <TextInput
            label="Name"
            placeholder="Ozempic label questions"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            required
            data-autofocus
          />
          <CollectionDocumentPicker
            label="Answer from these collections"
            value={collections}
            onChange={setCollections}
            allowDocumentSubset={false}
          />
          <Text size="xs" c="dimmed">
            Answers come only from these documents. You can change the collections later.
          </Text>
          <ChatModelPicker
            options={modelOptions}
            error={models.error}
            value={choice}
            onChange={setChoice}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setCreating(false)}>
              Cancel
            </Button>
            <Button onClick={() => void onCreate()} loading={busy} disabled={!canCreate}>
              Create chat
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
